'use client';

import { useState } from 'react';

import {
  ArchDiagram,
  type ArchEdge,
  type ArchNode,
} from '../../shared/arch-diagram';
import { SegmentedControl } from '../../shared/segmented-control';

/**
 * The decoupling, drawn: one monolith doing everything, then the engine
 * carved out as its own Cloudflare Worker with the queues, webhooks, signed
 * callback, Postgres, and the operator console around it.
 */

const BEFORE_NODES: ArchNode[] = [
  { id: 'client', x: 11, y: 10, title: 'Client', sub: 'browser · app' },
  { id: 'cdn', x: 38, y: 10, title: 'Cloudflare CDN', sub: 'edge' },
  {
    id: 'app',
    x: 76,
    y: 10,
    title: 'Monolith · Worker',
    sub: 'auth · billing · generation · retries',
    emphasis: 'solid',
  },
  {
    id: 'queue',
    x: 76,
    y: 48,
    title: 'One shared queue',
    sub: '5s delivery floor',
    emphasis: 'muted',
  },
  {
    id: 'providers',
    x: 76,
    y: 86,
    title: 'AI providers',
    sub: 'no breakers · no attempt log',
    emphasis: 'muted',
  },
];

const BEFORE_EDGES: ArchEdge[] = [
  {
    id: 'c-cdn',
    points: [
      [16, 10],
      [30, 10],
    ],
    pulse: true,
  },
  {
    id: 'cdn-app',
    points: [
      [46, 10],
      [63, 10],
    ],
    pulse: true,
  },
  {
    id: 'app-q',
    points: [
      [71, 15],
      [71, 42],
    ],
    label: 'everything in one lane',
    labelAt: [50, 29],
    pulse: true,
  },
  {
    id: 'q-p',
    points: [
      [71, 54],
      [71, 80],
    ],
    pulse: true,
  },
  {
    id: 'p-app',
    points: [
      [83, 80],
      [83, 16],
    ],
    label: 'webhooks · public URL',
    labelAt: [83, 65],
    dashed: true,
  },
];

const AFTER_NODES: ArchNode[] = [
  { id: 'client', x: 11, y: 10, title: 'Client', sub: 'browser · app' },
  { id: 'cdn', x: 38, y: 10, title: 'Cloudflare CDN', sub: 'edge' },
  {
    id: 'app',
    x: 76,
    y: 10,
    title: 'Main app · Worker',
    sub: 'auth · billing · refunds',
  },
  {
    id: 'engine',
    x: 76,
    y: 44,
    title: 'Reliability engine · Worker',
    sub: 'router · breakers · watchdog',
    emphasis: 'solid',
  },
  {
    id: 'console',
    x: 14,
    y: 44,
    title: 'Operator console',
    sub: '5 screens · live queries',
  },
  { id: 'queues', x: 40, y: 68, title: 'Queues', sub: '3 lanes · 0s floor' },
  {
    id: 'providers',
    x: 12,
    y: 86,
    title: 'AI providers',
    sub: 'tiers 1..3 per model',
    emphasis: 'muted',
  },
  {
    id: 'db',
    x: 76,
    y: 88,
    title: 'Postgres',
    sub: 'attempt log is the breaker',
  },
];

const AFTER_EDGES: ArchEdge[] = [
  {
    id: 'c-cdn',
    points: [
      [16, 10],
      [30, 10],
    ],
    pulse: true,
  },
  {
    id: 'cdn-app',
    points: [
      [46, 10],
      [63, 10],
    ],
    pulse: true,
  },
  {
    id: 'app-engine',
    points: [
      [70, 15],
      [70, 38],
    ],
    label: 'service binding · generate',
    labelAt: [55, 27],
    pulse: true,
  },
  {
    id: 'engine-app',
    points: [
      [84, 38],
      [84, 15],
    ],
    label: 'signed callback · 5 outcomes',
    labelAt: [84, 27],
    pulse: true,
  },
  {
    id: 'engine-queues',
    points: [
      [62, 48],
      [40, 48],
      [40, 62],
    ],
    label: 'dispatch',
    labelAt: [40, 55],
    pulse: true,
  },
  {
    id: 'queues-providers',
    points: [
      [33, 70],
      [12, 70],
      [12, 80],
    ],
    pulse: true,
  },
  {
    id: 'providers-engine',
    points: [
      [21, 88],
      [55, 88],
      [62, 50],
    ],
    label: 'webhooks',
    labelAt: [43, 82],
    pulse: true,
  },
  {
    id: 'engine-db',
    points: [
      [76, 50],
      [76, 82],
    ],
    label: 'all state',
    labelAt: [76, 66],
    pulse: true,
  },
  {
    id: 'console-engine',
    points: [
      [22, 47],
      [62, 47],
    ],
    label: 'admin API',
    labelAt: [42, 42],
    dashed: true,
  },
];

export function EngineArchitecture() {
  const [era, setEra] = useState<'before' | 'after'>('after');
  const before = era === 'before';

  return (
    <div className="px-4 pb-4">
      <div className="mb-3 flex justify-end">
        <SegmentedControl
          label="Architecture era"
          labelHidden
          value={era}
          onChange={setEra}
          options={[
            { value: 'before', label: 'Inside the monolith' },
            { value: 'after', label: 'Carved out' },
          ]}
        />
      </div>

      <ArchDiagram
        nodes={before ? BEFORE_NODES : AFTER_NODES}
        edges={before ? BEFORE_EDGES : AFTER_EDGES}
        heightClass="pb-[120%] sm:pb-[58%]"
        caption={
          before
            ? 'Before: one Worker owned everything, retries included. A provider hiccup was an app incident.'
            : 'After: the engine is its own stateless Worker. Any instance answers any request; losing one costs nothing.'
        }
      />
    </div>
  );
}
